(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const foilEnabled = document.body.classList.contains('pullex-motion-enabled');
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  // Progressive reveal. Motion is skipped cleanly when the OS asks for reduced motion.
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });
    qa('.pullex-reveal').forEach((element) => revealObserver.observe(element));
  } else {
    qa('.pullex-reveal').forEach((element) => element.classList.add('is-visible'));
  }

  // Card foil / depth. Only the media layer moves; product copy and commerce controls stay still.
  if (!reduceMotion && canHover && foilEnabled) {
    qa('[data-pullex-tilt], .product-card-wrapper').forEach((element) => {
      let frame;
      const move = (event) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = element.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
          const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
          element.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
          element.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
          element.style.setProperty('--ry', `${((x - 0.5) * 5).toFixed(2)}deg`);
          element.style.setProperty('--rx', `${((0.5 - y) * 4).toFixed(2)}deg`);
        });
      };
      const reset = () => {
        element.style.setProperty('--ry', '0deg');
        element.style.setProperty('--rx', '0deg');
        element.style.setProperty('--mx', '50%');
        element.style.setProperty('--my', '50%');
      };
      element.addEventListener('pointermove', move, { passive: true });
      element.addEventListener('pointerleave', reset, { passive: true });
    });
  }

  // Hero depth is deliberately tiny. It should feel dimensional, not like a game menu.
  const hero = q('[data-pullex-hero]');
  if (hero && !reduceMotion && canHover) {
    let frame;
    hero.addEventListener('pointermove', (event) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = hero.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        hero.style.setProperty('--hero-x', `${(x * 6).toFixed(2)}px`);
        hero.style.setProperty('--hero-y', `${(y * 4).toFixed(2)}px`);
      });
    }, { passive: true });
    hero.addEventListener('pointerleave', () => {
      hero.style.setProperty('--hero-x', '0px');
      hero.style.setProperty('--hero-y', '0px');
    }, { passive: true });
  }

  // Collector HQ status jumps to the in-page Live Hub instead of throwing shoppers out of the store.
  qa('[data-pullex-live-jump]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const target = q('#pullex-live-hub');
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => {
        target.focus({ preventScroll: true });
      }, reduceMotion ? 0 : 450);
      if (history.replaceState) history.replaceState(null, '', '#pullex-live-hub');
    });
  });

  // Current-state highlighting for custom navigation.
  qa('.pullex-utility-nav__links a').forEach((link) => {
    try {
      const path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') || '/';
      const here = window.location.pathname.replace(/\/$/, '') || '/';
      if (path === here) {
        link.classList.add('is-current');
        link.setAttribute('aria-current', 'page');
      }
    } catch (_) {}
  });

  // Show the shortcut users actually have on their keyboard.
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  const shortcutLabel = /Mac|iPhone|iPad|iPod/i.test(platform) ? '⌘K' : 'Ctrl K';
  qa('[data-pullex-shortcut-key]').forEach((element) => { element.textContent = shortcutLabel; });

  // PULLEX Chase Finder command palette.
  const finder = q('[data-pullex-finder]');
  const finderInput = finder ? q('[data-pullex-finder-input]', finder) : null;
  const finderResults = finder ? q('[data-pullex-finder-results]', finder) : null;
  const finderDefault = finder ? q('[data-pullex-finder-default]', finder) : null;
  const recentSearchShell = finder ? q('[data-pullex-recent-searches]', finder) : null;
  const recentSearchList = finder ? q('[data-pullex-recent-search-list]', finder) : null;
  const searchKey = 'pullex_searches_v1';
  let finderTimer;
  let finderAbort;
  let activeResultIndex = -1;
  let finderReturnFocus = null;

  const getRecentSearches = () => {
    try { return JSON.parse(localStorage.getItem(searchKey) || '[]'); } catch (_) { return []; }
  };
  const saveSearch = (term) => {
    const clean = String(term || '').trim();
    if (!clean) return;
    const next = [clean, ...getRecentSearches().filter((item) => item.toLowerCase() !== clean.toLowerCase())].slice(0, 6);
    try { localStorage.setItem(searchKey, JSON.stringify(next)); } catch (_) {}
    renderRecentSearches();
  };
  const renderRecentSearches = () => {
    if (!recentSearchShell || !recentSearchList) return;
    const terms = getRecentSearches();
    recentSearchShell.hidden = terms.length === 0;
    recentSearchList.replaceChildren();
    terms.forEach((term) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = term;
      button.addEventListener('click', () => {
        finderInput.value = term;
        finderInput.dispatchEvent(new Event('input', { bubbles: true }));
        finderInput.focus();
      });
      recentSearchList.appendChild(button);
    });
  };

  qa('[data-pullex-search-term]', finder || document).forEach((button) => {
    button.addEventListener('click', () => {
      const term = button.dataset.pullexSearchTerm || button.textContent || '';
      if (!finderInput) return;
      finderInput.value = term.trim();
      finderInput.dispatchEvent(new Event('input', { bubbles: true }));
      finderInput.focus();
    });
  });

  const openFinder = () => {
    if (!finder) {
      q('.header__search summary')?.click();
      return;
    }
    finderReturnFocus = document.activeElement;
    finder.removeAttribute('inert');
    finder.inert = false;
    finder.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pullex-finder-open');
    renderRecentSearches();
    window.setTimeout(() => finderInput?.focus(), 30);
  };
  const closeFinder = () => {
    if (!finder) return;
    finder.setAttribute('aria-hidden', 'true');
    finder.setAttribute('inert', '');
    finder.inert = true;
    finder.removeAttribute('aria-busy');
    document.body.classList.remove('pullex-finder-open');
    activeResultIndex = -1;
    if (finderReturnFocus && typeof finderReturnFocus.focus === 'function') finderReturnFocus.focus();
  };

  qa('[data-pullex-open-search]').forEach((button) => button.addEventListener('click', openFinder));
  qa('[data-pullex-close-search]').forEach((button) => button.addEventListener('click', closeFinder));
  document.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      openFinder();
      return;
    }
    if (!finder || finder.getAttribute('aria-hidden') !== 'false') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeFinder();
      return;
    }
    if (event.key === 'Tab') {
      const focusables = qa('a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled])', finder)
        .filter((element) => element.offsetParent !== null);
      if (focusables.length) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    const resultLinks = qa('[data-pullex-search-result]', finderResults || finder);
    if (!resultLinks.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      activeResultIndex += event.key === 'ArrowDown' ? 1 : -1;
      if (activeResultIndex < 0) activeResultIndex = resultLinks.length - 1;
      if (activeResultIndex >= resultLinks.length) activeResultIndex = 0;
      resultLinks[activeResultIndex].focus();
    }
  });

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100); }
    catch (_) { return `$${(Number(cents || 0) / 100).toFixed(2)}`; }
  };
  const searchEndpoint = () => {
    const base = window.routes?.predictive_search_url || '/search/suggest';
    return base.endsWith('.json') ? base : `${base}.json`;
  };

  const renderFinderResults = (data, term) => {
    if (!finderResults || !finderDefault) return;
    const resources = data?.resources?.results || {};
    const products = Array.isArray(resources.products) ? resources.products.slice(0, 6) : [];
    const collections = Array.isArray(resources.collections) ? resources.collections.slice(0, 4) : [];
    finderResults.replaceChildren();

    const heading = document.createElement('div');
    heading.className = 'pullex-finder__result-head';
    heading.innerHTML = `<span>SEARCH RESULTS</span><a href="/search?q=${encodeURIComponent(term)}">View all →</a>`;
    finderResults.appendChild(heading);

    if (!products.length && !collections.length) {
      const empty = document.createElement('div');
      empty.className = 'pullex-finder__empty';
      empty.innerHTML = `<strong>No direct hit yet.</strong><span>Press Enter to search the full shop for “${String(term).replace(/[&<>"']/g, '')}”.</span>`;
      finderResults.appendChild(empty);
    }

    if (products.length) {
      const label = document.createElement('div');
      label.className = 'pullex-finder__label';
      label.textContent = 'PRODUCTS';
      finderResults.appendChild(label);
      const grid = document.createElement('div');
      grid.className = 'pullex-finder__product-grid';
      products.forEach((product) => {
        const link = document.createElement('a');
        link.href = product.url;
        link.dataset.pullexSearchResult = '';
        link.className = 'pullex-finder__product';
        const imageUrl = (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url) || product.image || '';
        const media = document.createElement('span');
        media.className = 'pullex-finder__product-media';
        if (imageUrl) {
          const img = document.createElement('img');
          img.src = imageUrl;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }
        const copy = document.createElement('span');
        copy.className = 'pullex-finder__product-copy';
        const title = document.createElement('strong');
        title.textContent = product.title || 'Product';
        const price = document.createElement('small');
        if (product.price != null) price.textContent = typeof product.price === 'number' ? money(product.price) : String(product.price);
        copy.append(title, price);
        const arrow = document.createElement('span');
        arrow.className = 'pullex-finder__product-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↗';
        link.append(media, copy, arrow);
        grid.appendChild(link);
      });
      finderResults.appendChild(grid);
    }

    if (collections.length) {
      const label = document.createElement('div');
      label.className = 'pullex-finder__label';
      label.textContent = 'COLLECTIONS';
      finderResults.appendChild(label);
      const list = document.createElement('div');
      list.className = 'pullex-finder__collection-list';
      collections.forEach((collection) => {
        const link = document.createElement('a');
        link.href = collection.url;
        link.dataset.pullexSearchResult = '';
        const collectionTitle = document.createElement('span');
        collectionTitle.textContent = collection.title || 'Collection';
        const collectionArrow = document.createElement('span');
        collectionArrow.setAttribute('aria-hidden', 'true');
        collectionArrow.textContent = '→';
        link.append(collectionTitle, collectionArrow);
        list.appendChild(link);
      });
      finderResults.appendChild(list);
    }

    finderDefault.hidden = true;
    finderResults.hidden = false;
    activeResultIndex = -1;
  };

  finderInput?.addEventListener('input', () => {
    const term = finderInput.value.trim();
    window.clearTimeout(finderTimer);
    finderAbort?.abort();
    if (term.length < 2) {
      finderResults.hidden = true;
      finderDefault.hidden = false;
      return;
    }
    finderTimer = window.setTimeout(async () => {
      finderAbort = new AbortController();
      finder?.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product,collection',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'last'
        });
        const response = await fetch(`${searchEndpoint()}?${params.toString()}`, {
          credentials: 'same-origin',
          signal: finderAbort.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Search request failed');
        renderFinderResults(await response.json(), term);
      } catch (error) {
        if (error.name === 'AbortError') return;
        finderResults.hidden = false;
        finderDefault.hidden = true;
        finderResults.innerHTML = `<div class="pullex-finder__empty"><strong>Search is still available.</strong><a href="/search?q=${encodeURIComponent(term)}">Search the full shop →</a></div>`;
      } finally {
        finder?.removeAttribute('aria-busy');
      }
    }, 180);
  });
  q('[data-pullex-finder-form]')?.addEventListener('submit', () => saveSearch(finderInput?.value));

  // Horizontal product rails.
  qa('[data-pullex-rail]').forEach((rail) => {
    const viewport = q('[data-rail-viewport]', rail);
    if (!viewport) return;
    const amount = () => Math.min(viewport.clientWidth * 0.82, 920);
    q('[data-rail-prev]', rail)?.addEventListener('click', () => viewport.scrollBy({ left: -amount(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    q('[data-rail-next]', rail)?.addEventListener('click', () => viewport.scrollBy({ left: amount(), behavior: reduceMotion ? 'auto' : 'smooth' }));
  });

  // Long collection descriptions stay compact until the shopper asks for more.
  qa('[data-pullex-description-clamp]').forEach((shell) => {
    const content = q('[data-pullex-description-content]', shell);
    const button = q('[data-pullex-description-toggle]', shell);
    if (!content || !button) return;
    const evaluate = () => {
      if (shell.classList.contains('is-expanded')) return;
      button.hidden = content.scrollHeight <= content.clientHeight + 4;
    };
    button.addEventListener('click', () => {
      const expanded = shell.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Show less' : 'Read more';
    });
    window.setTimeout(evaluate, 50);
    window.addEventListener('resize', evaluate, { passive: true });
  });

  // Mobile sticky add-to-cart mirrors Dawn's real form and only appears after the native CTA leaves view.
  qa('[data-pullex-sticky-atc]').forEach((bar) => {
    const sectionId = bar.dataset.sectionId;
    const button = q('[data-pullex-sticky-add]', bar);
    const stickyPrice = q('[data-pullex-sticky-price]', bar);
    const productRoot = q(`#MainProduct-${CSS.escape(sectionId)}`);
    if (!button || !productRoot) return;

    let observedSubmit = null;
    const visibilityObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => bar.classList.toggle('is-visible', !entry.isIntersecting));
        }, { threshold: 0.05 })
      : null;

    const observeSubmit = (original) => {
      if (!original || original === observedSubmit) return;
      if (observedSubmit && visibilityObserver) visibilityObserver.unobserve(observedSubmit);
      observedSubmit = original;
      if (visibilityObserver) visibilityObserver.observe(original);
      else bar.classList.add('is-visible');
    };

    const sync = () => {
      const original = q('.product-form__submit', productRoot);
      const priceNode = q(`#price-${CSS.escape(sectionId)} .price-item--sale`, productRoot) || q(`#price-${CSS.escape(sectionId)} .price-item--regular`, productRoot);
      if (priceNode && stickyPrice) stickyPrice.textContent = priceNode.textContent.trim();
      if (!original) { bar.classList.remove('is-visible'); return; }
      observeSubmit(original);
      button.disabled = original.disabled;
      const originalText = q('span', original)?.textContent?.trim();
      button.textContent = originalText || (original.disabled ? 'Sold Out' : 'Add to Cart');
    };
    button.addEventListener('click', () => q('.product-form__submit', productRoot)?.click());
    const observer = new MutationObserver(() => window.requestAnimationFrame(sync));
    observer.observe(productRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    sync();
  });

  // Recently viewed collector trail.
  const recentShell = q('[data-pullex-recently-viewed]');
  const currentScript = q('#PullexCurrentProduct');
  const recentKey = 'pullex_recent_v2';
  let current = null;
  if (currentScript) {
    try { current = JSON.parse(currentScript.textContent); } catch (_) { current = null; }
  }
  let handles = [];
  try { handles = JSON.parse(localStorage.getItem(recentKey) || '[]'); } catch (_) { handles = []; }
  if (current?.handle) {
    handles = [current.handle, ...handles.filter((handle) => handle !== current.handle)].slice(0, 8);
    try { localStorage.setItem(recentKey, JSON.stringify(handles)); } catch (_) {}
  }
  const renderRecent = async () => {
    if (!recentShell) return;
    const targets = handles.filter((handle) => handle && handle !== current?.handle).slice(0, 4);
    if (!targets.length) return;
    const grid = q('[data-pullex-recent-grid]', recentShell);
    if (!grid) return;
    const products = await Promise.all(targets.map(async (handle) => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
        return response.ok ? response.json() : null;
      } catch (_) { return null; }
    }));
    const currency = window.Shopify?.currency?.active || 'USD';
    const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
    grid.replaceChildren();
    products.filter(Boolean).forEach((product) => {
      const link = document.createElement('a');
      link.className = 'pullex-recent-card';
      link.href = product.url;
      const media = document.createElement('span');
      media.className = 'pullex-recent-card__media';
      const imageUrl = product.featured_image || product.images?.[0];
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      const copy = document.createElement('span');
      copy.className = 'pullex-recent-card__copy';
      const title = document.createElement('strong');
      title.textContent = product.title;
      const price = document.createElement('small');
      price.textContent = typeof product.price === 'number' ? formatter.format(product.price / 100) : '';
      copy.append(title, price);
      const arrow = document.createElement('span');
      arrow.className = 'pullex-recent-card__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↗';
      link.append(media, copy, arrow);
      grid.appendChild(link);
    });
    if (grid.children.length) recentShell.hidden = false;
  };
  renderRecent();
  q('[data-pullex-clear-recent]')?.addEventListener('click', () => {
    try { localStorage.removeItem(recentKey); } catch (_) {}
    if (recentShell) recentShell.hidden = true;
  });

  // Collector Proof review feed. Tokens never live in the storefront: this only consumes a public/sanitized JSON feed.
  qa('[data-pullex-review-wall]').forEach(async (wall) => {
    const feedUrl = wall.dataset.reviewFeed;
    const grid = q('[data-review-grid]', wall);
    const empty = q('[data-review-empty]', wall);
    const moreShell = q('[data-review-more-shell]', wall);
    const moreButton = q('[data-review-more]', wall);
    const countLabel = q('[data-review-count-label]', wall);
    if (!feedUrl || !grid) return;
    try {
      const response = await fetch(feedUrl, { credentials: feedUrl.startsWith(window.location.origin) ? 'same-origin' : 'omit' });
      if (!response.ok) return;
      const payload = await response.json();
      const summary = q('[data-review-summary]', wall);
      const snapshot = q('[data-review-snapshot]', wall);
      if (!Array.isArray(payload) && summary) {
        const summaryParts = [];
        if (payload.aggregate_rating) summaryParts.push(`${payload.aggregate_rating}/5`);
        if (payload.rating_count) summaryParts.push(`${Number(payload.rating_count).toLocaleString()} ratings`);
        if (payload.five_star_count) summaryParts.push(`${Number(payload.five_star_count).toLocaleString()} five-star`);
        if (summaryParts.length) summary.textContent = summaryParts.join(' · ');
      }
      if (!Array.isArray(payload) && snapshot && payload.snapshot) {
        const parsedSnapshot = new Date(`${payload.snapshot}T00:00:00Z`);
        const prettySnapshot = Number.isNaN(parsedSnapshot.getTime())
          ? payload.snapshot
          : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(parsedSnapshot);
        snapshot.textContent = `TikTok Shop review export through ${prettySnapshot}`;
        snapshot.hidden = false;
      }
      const rawReviews = Array.isArray(payload) ? payload : (Array.isArray(payload.reviews) ? payload.reviews : []);
      const trustTerms = ['pull','pack','banger','hit','shipping','ship','package','packaging','service','seller','sticker','care','fast','secure','condition'];
      const scoredReviews = rawReviews.map((review, index) => {
        const text = String(review?.text || '').toLowerCase();
        const trustHits = trustTerms.reduce((score, term) => score + (text.includes(term) ? 1 : 0), 0);
        const quality = Number(review?.rating || 0) * 10 + trustHits * 5 + (text.length > 80 ? 2 : 0);
        return { review, index, quality };
      }).filter((entry) => entry.review?.text);
      const recentReviews = scoredReviews.slice(0, 12);
      const strongestReviews = [...scoredReviews].sort((a, b) => b.quality - a.quality || a.index - b.index);
      const mixedReviews = [];
      const seenReviews = new Set();
      const pushUnique = (entry) => {
        const key = `${entry.index}:${String(entry.review?.text || '').slice(0, 80)}`;
        if (!seenReviews.has(key)) { seenReviews.add(key); mixedReviews.push(entry.review); }
      };
      // First impression = fresh proof plus the strongest trust-building feedback.
      [recentReviews[0], strongestReviews[0], recentReviews[1], strongestReviews[1], recentReviews[2], strongestReviews[2], recentReviews[3], strongestReviews[3]]
        .filter(Boolean)
        .forEach(pushUnique);
      recentReviews.forEach(pushUnique);
      strongestReviews.forEach(pushUnique);
      const reviews = mixedReviews;
      const existing = grid.querySelectorAll('.pullex-review-card').length;
      let cursor = 0;
      const batchSize = 8;
      const createReview = (review) => {
        if (!review || !review.text) return null;
        const article = document.createElement('article');
        article.className = 'pullex-review-card pullex-review-card--feed';
        const rating = Math.max(1, Math.min(5, Number(review.rating || 5)));
        const top = document.createElement('div');
        top.className = 'pullex-review-card__top';
        const stars = document.createElement('span');
        stars.className = 'pullex-review-card__stars';
        stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        stars.innerHTML = [1,2,3,4,5].map((n) => `<span aria-hidden="true"${n > rating ? ' class="is-empty"' : ''}>★</span>`).join('');
        const source = document.createElement('span');
        source.className = 'pullex-review-card__source';
        source.textContent = review.source || payload.source || 'TikTok Shop';
        top.append(stars, source);
        const quote = document.createElement('blockquote');
        quote.textContent = `“${String(review.text).trim()}”`;
        const foot = document.createElement('footer');
        const avatar = document.createElement('span');
        avatar.className = 'pullex-review-card__avatar pullex-review-card__avatar--initial';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = String(review.name || 'Collector').trim().charAt(0).toUpperCase() || 'C';
        const meta = document.createElement('span');
        const name = document.createElement('strong');
        name.textContent = review.name || 'Verified Collector';
        const small = document.createElement('small');
        small.textContent = [review.product, review.date].filter(Boolean).join(' · ');
        meta.append(name, small);
        foot.append(avatar, meta);
        if (review.url) {
          const link = document.createElement('a');
          link.href = review.url; link.target = '_blank'; link.rel = 'noopener'; link.setAttribute('aria-describedby','a11y-new-window-message'); link.setAttribute('aria-label','Open source review'); link.textContent='↗';
          foot.appendChild(link);
        }
        article.append(top, quote, foot);
        return article;
      };
      const renderBatch = (limit) => {
        let added = 0;
        while (cursor < reviews.length && added < limit) {
          const card = createReview(reviews[cursor++]);
          if (card) { grid.appendChild(card); added += 1; }
        }
        const visibleFeed = grid.querySelectorAll('.pullex-review-card--feed').length;
        const totalVisible = existing + visibleFeed;
        const writtenTotal = Number(payload.written_review_count || reviews.length);
        if (countLabel) countLabel.textContent = `Showing ${Math.min(totalVisible, writtenTotal)} of ${writtenTotal} written TikTok Shop reviews`;
        if (moreShell) moreShell.hidden = cursor >= reviews.length;
        if (grid.querySelector('.pullex-review-card, .pullex-proof__app')) empty?.setAttribute('hidden','');
      };
      renderBatch(Math.max(0, batchSize - existing));
      moreButton?.addEventListener('click', () => renderBatch(batchSize));
    } catch (_) {
      // Keep the honest TikTok Shop source CTA visible if the feed is unavailable.
    }
  });

  // Product-level collector proof. Same sanitized review feed, but ranked by the product context.
  qa('[data-pullex-product-proof]').forEach(async (shell) => {
    const feedUrl = shell.dataset.reviewFeed;
    const context = shell.dataset.reviewContext || 'trust';
    const grid = q('[data-product-review-grid]', shell);
    if (!feedUrl || !grid) return;
    const termsByContext = {
      packs: ['pull','pulled','banger','pack','bulk','stream','sticker','rip','hit'],
      tcg: ['pull','box','cards','pack','shipping','package','condition','fast'],
      apparel: ['quality','amazing','service','shipping','seller','package'],
      trust: ['shipping','package','packaging','service','seller','fast','secure','care','condition']
    };
    try {
      const response = await fetch(feedUrl, { credentials: feedUrl.startsWith(window.location.origin) ? 'same-origin' : 'omit' });
      if (!response.ok) return;
      const payload = await response.json();
      const raw = Array.isArray(payload) ? payload : (Array.isArray(payload.reviews) ? payload.reviews : []);
      const terms = termsByContext[context] || termsByContext.trust;
      const ranked = raw.map((review, index) => {
        const text = String(review?.text || '').toLowerCase();
        const hits = terms.reduce((score, term) => score + (text.includes(term) ? 1 : 0), 0);
        return { review, index, score: hits * 8 + Number(review?.rating || 0) * 2 + (text.length > 70 ? 1 : 0) };
      }).filter((entry) => entry.review?.text && Number(entry.review?.rating || 0) >= 4).sort((a,b) => b.score - a.score || a.index - b.index).slice(0,3);
      if (!ranked.length) { shell.hidden = true; return; }
      grid.replaceChildren();
      ranked.forEach(({ review }) => {
        const article = document.createElement('article');
        article.className = 'pullex-product-proof__card';
        const stars = document.createElement('div');
        stars.className = 'pullex-review-card__stars';
        const rating = Math.max(1, Math.min(5, Number(review.rating || 5)));
        stars.setAttribute('aria-label', `${rating} out of 5 stars`);
        stars.innerHTML = [1,2,3,4,5].map((n) => `<span aria-hidden="true"${n > rating ? ' class="is-empty"' : ''}>★</span>`).join('');
        const quote = document.createElement('blockquote');
        quote.textContent = `“${String(review.text).trim()}”`;
        const meta = document.createElement('div');
        const buyer = document.createElement('strong');
        buyer.textContent = review.name || 'Verified TikTok Buyer';
        const sourceLine = document.createElement('small');
        sourceLine.textContent = `${review.date || 'TikTok Shop'} · TikTok Shop`;
        meta.append(buyer, sourceLine);
        article.append(stars, quote, meta);
        grid.appendChild(article);
      });
    } catch (_) {
      shell.hidden = true;
    }
  });

})();
